import { useEffect, useRef } from 'react';
import type { RxDocument } from 'rxdb';

import { isArchived } from '@flintmint/core-shared/rxdb';

export const useLeaveOnArchived = <T>(doc: RxDocument<T> | null | undefined, onLeave: () => void) => {
  const onLeaveRef = useRef(onLeave);
  onLeaveRef.current = onLeave;

  // Leaving happens once per archived stretch, not once per subscription: a host that saves before
  // leaving gets a new snapshot of the archived document, and `doc.$` replays the archived state on
  // subscribe. A second onLeave would navigate twice. A non-archived emission (a restore) or a
  // different document re-arms the guard.
  const hasLeftRef = useRef(false);
  const documentIdRef = useRef(doc?.primary);

  useEffect(() => {
    if (documentIdRef.current !== doc?.primary) {
      documentIdRef.current = doc?.primary;
      hasLeftRef.current = false;
    }

    const subscription = doc?.$?.subscribe((data) => {
      if (!isArchived(data)) {
        hasLeftRef.current = false;
        return;
      }

      if (hasLeftRef.current) return;

      hasLeftRef.current = true;
      onLeaveRef.current();
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, [doc]);
};
